import React, { useMemo } from 'react';
import { GenericFilterProps, AmenitiesBody, AmenitySection, BaseConfig, Amenity } from '../../types';
import { useScreenBreakpoint, BreakpointValue } from 'consts/breakpoints';
import { MarketplaceType } from 'utils/entities';
import FilterSection from '../../common/DropdownSection';
import { useLocale } from 'locale';
import { isFilterActiveByType } from '../../utils';
import * as Styled from './styled';
import CheckboxGroup from './checkbox-group';
import { Section } from '../../styled/common';
import { CheckboxChip, CheckboxChipGroup } from './checkbox-chip/CheckboxChip';
import { without } from 'lodash';
import ParkingIcon from 'assets/svg/combined-filters-search-view/Parking.svg';
import ElevatorIcon from 'assets/svg/combined-filters-search-view/Elevator.svg';
import BalconyIcon from 'assets/svg/combined-filters-search-view/Balcony.svg';
import SecureRoomIcon from 'assets/svg/combined-filters-search-view/SecureRoom.svg';
import MamakIcon from 'assets/svg/filters/secure_1.svg';
import MiklatIcon from 'assets/svg/filters/secure_2.svg';
import AirCondIcon from 'assets/svg/combined-filters-search-view/AirCond.svg';
import StorageIcon from 'assets/svg/combined-filters-search-view/Storage.svg';
import AccessibleIcon from 'assets/svg/combined-filters-search-view/Accessible.svg';
import GratingIcon from 'assets/svg/combined-filters-search-view/Grating.svg';
import FullTimeAccessIcon from 'assets/svg/combined-filters-search-view/FullTimeAccess.svg';
import AlarmIcon from 'assets/svg/combined-filters-search-view/Alarm.svg';
import SubDivisibleIcon from 'assets/svg/combined-filters-search-view/SubDivisible.svg';
import DoormanIcon from 'assets/svg/combined-filters-search-view/Doorman.svg';
import ReceptionIcon from 'assets/svg/combined-filters-search-view/Reception.svg';
import ConferenceRoomIcon from 'assets/svg/combined-filters-search-view/ConferenceRoom.svg';
import KitchenetteIcon from 'assets/svg/combined-filters-search-view/Kitchenette.svg';
import OutdoorSpaceIcon from 'assets/svg/combined-filters-search-view/PrivateBalcony.svg';
import ColdRoomIcon from 'assets/svg/combined-filters-search-view/ColdRoom.svg';
import LoadingRampIcon from 'assets/svg/combined-filters-search-view/LoadingRamp.svg';
import HighCeilingIcon from 'assets/svg/combined-filters-search-view/HighCeiling.svg';
import ActiveBusinessIcon from 'assets/svg/combined-filters-search-view/ActiveBusiness.svg';
import ParkingBikesIcon from 'assets/svg/combined-filters-search-view/ParkingBikes.svg';

const combinedFiltersSearchAmenityToIcon: Partial<Record<Amenity, React.ReactNode>> = {
  [Amenity.Parking]: <ParkingIcon />,
  [Amenity.Elevator]: <ElevatorIcon />,
  [Amenity.Balcony]: <BalconyIcon />,
  [Amenity.SecureRoom]: <SecureRoomIcon />,
  [Amenity.Mamak]: <MamakIcon />,
  [Amenity.Miklat]: <MiklatIcon />,
  [Amenity.AirConditioner]: <AirCondIcon />,
  [Amenity.Storage]: <StorageIcon />,
  [Amenity.Grating]: <GratingIcon />,
  [Amenity.FullTimeAccess]: <FullTimeAccessIcon />,
  [Amenity.Alarm]: <AlarmIcon />,
  [Amenity.SubDivisible]: <SubDivisibleIcon />,
  [Amenity.Accessible]: <AccessibleIcon />,
  [Amenity.Doorman]: <DoormanIcon />,
  [Amenity.Reception]: <ReceptionIcon />,
  [Amenity.ConferenceRoom]: <ConferenceRoomIcon />,
  [Amenity.Kitchenette]: <KitchenetteIcon />,
  [Amenity.OuterSpace]: <OutdoorSpaceIcon />,
  [Amenity.ColdRoom]: <ColdRoomIcon />,
  [Amenity.LoadingRamp]: <LoadingRampIcon />,
  [Amenity.HighCeiling]: <HighCeilingIcon />,
  [Amenity.ActiveBusiness]: <ActiveBusinessIcon />,
  [Amenity.ParkingVisitors]: <ParkingIcon />,
  [Amenity.ParkingEmployee]: <ParkingIcon />,
  [Amenity.ParkingBikes]: <ParkingBikesIcon />,
};

function getConfigByBreakPoint(breakpoint: BreakpointValue = 5, conf: BaseConfig): AmenitiesBody {
  let data = conf.amenities[breakpoint];
  let maybePrevBreakpoint = breakpoint - 1;

  while (!data) {
    data = conf.amenities[maybePrevBreakpoint--];
  }
  return data;
}

function renderSection(section: AmenitySection, props: AmenitiesProps, isLast: boolean) {
  switch (section.type) {
    case 'checkboxGroup':
      return (
        <CheckboxGroup
          last={isLast}
          group={section}
          values={props.intermediateValue}
          valueSetter={props.setIntermediateValue}
        />
      );
    default:
      break;
  }
  return null;
}

interface AmenitiesProps extends GenericFilterProps<'amenities'> {}

const Amenities: React.FC<AmenitiesProps> = (props) => {
  const {
    resetValue,
    commitValues,
    commitedValue,
    intermediateValue,
    setIntermediateValue,
    initialValue,
    alignment,
    type,
    isInline,
    marketplace,
    config,
  } = props;

  const breakpoint = useScreenBreakpoint();
  const isCommercial = marketplace === MarketplaceType.Commercial;
  const { t } = useLocale();
  const amenitiesConfig = useMemo(
    () => getConfigByBreakPoint(breakpoint, config),
    [ getConfigByBreakPoint, breakpoint, config ]
  );

  const isCombinedFiltersSearchView = useScreenBreakpoint() < 3;

  const labelTerm = isCommercial ? 'filters.commercial.amenities' : 'filters.amenities';
  const content = (
    <>
      {amenitiesConfig.title ? (
        <Styled.Header weight="bold" combinedFiltersSearchMode={isCombinedFiltersSearchView}>
          {t(amenitiesConfig.title)}
        </Styled.Header>
      ) : null}
      {isCombinedFiltersSearchView ? (
        <CheckboxChipGroup>
          {amenitiesConfig.sections.flatMap(section =>
            section.items.map(amenity => (
              <CheckboxChip
                key={amenity.amenity}
                label={t('filters.amenitiesOptions', { value: amenity.amenity })}
                icon={combinedFiltersSearchAmenityToIcon[amenity.amenity]}
                checked={intermediateValue.includes(amenity.amenity)}
                onClick={() => setIntermediateValue(intermediateValue.includes(amenity.amenity)
                  ? without(intermediateValue, amenity.amenity)
                  : intermediateValue.concat(amenity.amenity))}
              />
            ))
          )}
        </CheckboxChipGroup>
      ) : (
        <>
          {amenitiesConfig.sections.map((item, i, arr) => (
            <React.Fragment key={i}>
              {renderSection(item, props, (arr.length - 1) === i)}
            </React.Fragment>
          ))}
        </>
      )}
    </>
  );

  if (isInline) {
    return (
      <Section combinedFiltersSearchMode={isCombinedFiltersSearchView}>
        {content}
      </Section>
    );
  }

  return (
    <FilterSection
      isClearButtonDisabled={!isFilterActiveByType.amenities(intermediateValue, initialValue)}
      type={type}
      alignment={alignment}
      icon={config.filtersIcons[type]}
      onClear={resetValue}
      label={t(labelTerm, { count: commitedValue.length })}
      onSubmit={commitValues}
      isActive={isFilterActiveByType.amenities(commitedValue, initialValue)}
    >
      {content}
    </FilterSection>
  );
};

export default Amenities;
